import { Stack } from "@mui/material";
import { GoogleMap } from "@react-google-maps/api";
import { logEvent } from "@src/appV2/lib/analytics";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { USER_EVENTS } from "@src/constants";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { GoogleMapCustomMarkerWrapper } from "../../GeoLocation/GoogleMapCustomMarkerWrapper";
import { MapMarkerCounter } from "../../GeoLocation/MapMarkerCounter";
import { MapMarkerLocation } from "../../GeoLocation/MapMarkerLocation";
import { useWorkerLocations } from "../../GeoLocation/useWorkerLocations";
import { DEFAULT_MAP_ZOOM, googleMapOptions } from "../../ShiftDiscovery/MapView/constants";
import { ShiftDiscoveryMapViewEmptyStateCard } from "../../ShiftDiscovery/MapView/EmptyStateCard";
import { ShiftDiscoveryMapViewMapControls } from "../../ShiftDiscovery/MapView/MapControls";
import { SHIFT_DISCOVERY_IN_HOME_VISIT_MODAL_PATH } from "../../ShiftDiscovery/paths";
import { useInHomeVisitModalsDataContext } from "../../ShiftDiscovery/useInHomeVisitModalsDataContext";
import { type VisitWithCaseData } from "../types";
import { getVisitsQueryKey } from "../useGetVisitsWithCaseData";
import { useVisitsListData } from "../useVisitsListData";
import type { VisitLocation, VisitLocationCluster } from "./clustering/types";
import { useVisitLocationClusters } from "./clustering/useVisitLocationClusters";
import { VisitsAtLocationDrawer } from "./VisitsAtLocationDrawer";

export function InHomeVisitsMap() {
  const [map, setMap] = useState<google.maps.Map | undefined>(undefined);
  const queryClient = useQueryClient();
  const history = useHistory();

  const { generateModalPath } = useInHomeVisitModalsDataContext();

  const { workerLocation, deviceLocation, homeLocation, shouldRequestLocationPermission } =
    useWorkerLocations();

  const {
    visits,
    caseIdToPatientMap,
    caseIdToSpecialtiesMap,
    caseIdToWorkplaceNameMap,
    caseIdToWorkplaceIdMap,
    isVisitsLoading,
    isVisitsSuccess,
  } = useVisitsListData({
    booked: "false",
  });

  const visitLocations: VisitLocation[] = useMemo(() => {
    const groups = new Map<string, VisitLocation>();

    visits.forEach((visit) => {
      const patient = caseIdToPatientMap.get(visit.attributes.caseId);
      if (!patient) {
        return;
      }

      const key = `${patient.attributes.latitude},${patient.attributes.longitude}`;
      const existing = groups.get(key);
      if (existing) {
        existing.visits.push(visit);
      } else {
        groups.set(key, {
          id: key,
          location: {
            latitude: patient.attributes.latitude,
            longitude: patient.attributes.longitude,
          },
          visits: [visit],
        });
      }
    });

    return Array.from(groups.values());
  }, [visits, caseIdToPatientMap]);

  const clusters = useVisitLocationClusters(visitLocations, map);

  const initializedFitBounds = useRef(false);

  useEffect(() => {
    if (!map || initializedFitBounds.current || visitLocations.length === 0) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    visitLocations.forEach((loc) => {
      bounds.extend(new google.maps.LatLng(loc.location.latitude, loc.location.longitude));
    });

    if (workerLocation) {
      bounds.extend(workerLocation);
    }

    initializedFitBounds.current = true;
    map.fitBounds(bounds);
  }, [map, visitLocations, workerLocation]);

  const drawerModalState = useModalState("closed");
  const [selectedClusterVisits, setSelectedClusterVisits] = useState<VisitWithCaseData[]>([]);

  function onVisitClick(visitId: string, caseId: string) {
    logEvent(USER_EVENTS.TAP_VISIT_PIN, {
      visitId,
      caseId,
      trigger: "In-Home Map View",
    });

    history.push(
      generateModalPath(SHIFT_DISCOVERY_IN_HOME_VISIT_MODAL_PATH, {
        visitId,
        caseId,
      })
    );
  }

  function onClusterClick(cluster: VisitLocationCluster) {
    const allVisits = cluster.markers.flatMap((m) => m.visitLocation.visits);

    if (allVisits.length === 1) {
      onVisitClick(allVisits[0].id, allVisits[0].attributes.caseId);
      return;
    }

    logEvent(USER_EVENTS.TAP_VISIT_PIN, {
      visitCount: allVisits.length,
      trigger: "In-Home Map View Cluster",
    });
    setSelectedClusterVisits(allVisits);
    drawerModalState.openModal();
  }

  return (
    <Stack sx={{ position: "relative", flex: 1 }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        options={googleMapOptions}
        onLoad={(loadedMap) => {
          setMap(loadedMap);
          if (workerLocation) {
            loadedMap.setCenter(workerLocation);
          }
        }}
        onUnmount={() => {
          setMap(undefined);
        }}
      >
        {workerLocation && (
          <GoogleMapCustomMarkerWrapper position={workerLocation}>
            <MapMarkerLocation />
          </GoogleMapCustomMarkerWrapper>
        )}

        {clusters.map((cluster) => (
          <GoogleMapCustomMarkerWrapper key={cluster.id} position={cluster.position}>
            <MapMarkerCounter
              count={cluster.totalVisits}
              onClick={() => {
                onClusterClick(cluster);
              }}
            />
          </GoogleMapCustomMarkerWrapper>
        ))}
      </GoogleMap>

      <ShiftDiscoveryMapViewMapControls
        canCenterToDevice={
          (Boolean(deviceLocation) && Boolean(map)) || shouldRequestLocationPermission
        }
        canCenterToHome={Boolean(homeLocation) && Boolean(map)}
        isLoading={isVisitsLoading}
        onCenterToHome={() => {
          if (map && homeLocation) {
            map.panTo(homeLocation);
            map.setZoom(DEFAULT_MAP_ZOOM);
            void queryClient.invalidateQueries(getVisitsQueryKey);
          }
        }}
        onCenterToDevice={() => {
          if (map && deviceLocation) {
            map.panTo(deviceLocation);
            map.setZoom(DEFAULT_MAP_ZOOM);
            void queryClient.invalidateQueries(getVisitsQueryKey);
          }
        }}
        onZoomIn={() => {
          const currentZoom = map?.getZoom();
          if (map && currentZoom) {
            map.setZoom(currentZoom + 1);
          }
        }}
        onZoomOut={() => {
          const currentZoom = map?.getZoom();
          if (map && currentZoom) {
            map.setZoom(currentZoom - 1);
          }
        }}
      />

      <ShiftDiscoveryMapViewEmptyStateCard
        isVisible={isVisitsSuccess && visits.length === 0}
        primaryText="There are currently no open visits in your area."
        secondaryText="Check back later for new opportunities."
      />

      <VisitsAtLocationDrawer
        modalState={drawerModalState}
        visits={selectedClusterVisits}
        caseIdToPatientMap={caseIdToPatientMap}
        caseIdToSpecialtiesMap={caseIdToSpecialtiesMap}
        caseIdToWorkplaceNameMap={caseIdToWorkplaceNameMap}
        caseIdToWorkplaceIdMap={caseIdToWorkplaceIdMap}
      />
    </Stack>
  );
}
